import ControllerBase from "@/controllers/ControllerBase";
import {ILoginInput} from "@/types/Api/ILoginInput";
import IAuthResponse from "@/types/Api/IAuthResponse";
import IUser from "@/types/Models/Account/IUser";
import router from "@/router";
import Notifier from "@/plugins/notifications/Notifier";
import {Mandant} from "@/types/Models/Mandant/Mandant";
import {ModuleList} from "@/models/settings/ModuleList";
import {useAuthStore} from "@/stores/Auth";
import {PermissionList} from "@/models/settings/PermissionList";
import {MandantRolePermission} from "@/types/Models/Permissions/MandantRolePermission";
import {useQuery} from "@tanstack/vue-query";
import {QueryKeys} from "@/models/settings/QueryKeys";
import { MandantSetting } from "@/types/Models/Mandant/MandantSetting";

/**
 * Controller für die Authentifizierung / Autorisierung
 *
 * @see MaxWebBackend AuthController
 * @see MaxWebBackend UsersController
 */
class AuthController extends ControllerBase {
    constructor() {
        super();
    }

    /**
     * Benutzer anmelden mit E-Mail, Passwort
     *
     * @remarks POST: api/auth/login
     * @returns IAuthResponse oder StatusCode bei Fehler
     */
    public async login(user: ILoginInput): Promise<any> {
        const response = await this.api.httpClient.post<IAuthResponse>("auth/login", user);
        console.log("login response", response);
        if (response == undefined) return null;
        return response.data;
    }

    /**
     * Benutzer abmelden und den authStore löschen
     *
     * @remarks Keine REST Abfrage, die Tokens werden lokal gelöscht
     * @returns Weiterleitung zu "welcome" Seite
     */
    public async logout() {
        await this.authStore.clearStorage();
        Notifier.showSuccess({message: this.i18n.t("settings.notifications.logout")});
        //
        return router.replace({name: "welcome"});
    }

    /**
     * Gibt den eigenen WebUser zurück
     *
     * @remarks GET: api/users/me
     * @returns WebUser
     */
    public async getMe() {
        const response = await this.api.httpClient.get<IUser>(`users/me`, {headers: await this.getAuthHeader()});
        return response.data;
    }

    /**
     * Gibt alle Mandanten zurück, bei denen der WebUser registriert ist
     *
     * @remarks GET: api/users/mandanten
     * @returns WebUser
     */
    public async getMandanten() {
        const response = await this.api.httpClient.get<Mandant[]>(`users/mandanten`, {
            headers: await this.getAuthHeader(),
        });
        console.log("getMandanten response", response.data);
        return response.data;
    }

     public async getMandantSetting(): Promise<MandantSetting | null> {
        const store = useAuthStore();
        const mandant = store.activeMandant;
        if(mandant == null || mandant.mandantUser == undefined) return null;
        const response = await this.api.httpClient.get<MandantSetting>(
            `mandanten/${mandant?.mandantId}/setting`,
            {
                headers: await this.getAuthHeader(),
            }
        );
        return response.data;
    }

    /**
     * Gibt den HeaderImage des aktuellen Mandanten zurück
     *
     * @remarks GET: api/users/mandanten
     * @returns WebUser
     */
    public async getMandantHeaderImage(): Promise<string> {
        const store = useAuthStore();
        const mandant = store.activeMandant;
        const response = await this.api.httpClient.get<string>(`users/mandanten/${mandant.mandantId}/headerImage`, {
            headers: await this.getAuthHeader(),
        });
        return response.data;
    }

    public async getMandantFooterImage(): Promise<string> {
        const store = useAuthStore();
        const mandant = store.activeMandant;
        const response = await this.api.httpClient.get<string>(`users/mandanten/${mandant.mandantId}/footerImage`, {
            headers: await this.getAuthHeader(),
        });
        return response.data;
    }

    /**
     * Überprüfen, ob der aktive Mandant auf das Modul zugreifen darf
     */
    public static hasModuleAccess(moduleId: ModuleList) {
        const store = useAuthStore();
        const mandant = store.activeMandant;
        if (mandant == null || mandant.modules == null) return false;
        for (const module of mandant.modules) {
            if (module.moduleId == moduleId) {
                return true;
            }
        }
        return false;
    }

    /**
     * Überprüfen ob der Benutzer die entsprechende Berechtigung besitzt
     */
    public static hasUserAccess(moduleId: ModuleList, permission: PermissionList) {
        // Modulberechtigung überprüfen
        if (!AuthController.hasModuleAccess(moduleId)) return false;

        // Benutzerberechtigung überprüfen
        const store = useAuthStore();
        const mandant = store.activeMandant;
        if (mandant == null || mandant.mandantUser == null || mandant.mandantUser.userRole == null) return false;
        const permissions = store.activeRolePermissions;
        if (permissions == null) return false;
        // Berechtigung nach der Id suchen und überprüfen, ob diese erlaubt ist
        const hasAccess = permissions.find((rolePerm: MandantRolePermission) => {
            return rolePerm.permissionId == permission && rolePerm.isAllowed;
        });
        if (hasAccess === undefined) return false;

        // Benutzer ist berechtigt
        return true;
    }


    public static getActivePermissions(): MandantRolePermission[] | null {
        const store = useAuthStore();
        return store.activeRolePermissions;
    }

    public useMandantQuery() {
        return useQuery({queryKey: [QueryKeys.MANDANT_LIST], queryFn: () => this.getMandanten()});
    }


    /* TODO: loginQuery refactoring
    public useLoginQuery(user: ILoginInput) {
        return useQuery(["login", user], () => this.login(user), {
            select: (data) => data,
        });
    }*/
}

export default AuthController;
